// Mode objectives on the client: hill / bomb world markers and the HUD objective line with its
// direction arrow. Fed by the mode snapshot of every snapshot, read ~10 times a second.
//
// KOTH: the snapshot names the hill in play and the tick it hands over (or starts scoring). The
// next hill is the next one in the map's list; it is outlined in the world and named in the HUD
// during the last `HILL_PREVIEW_SEC` seconds, the same window the server announces it in.

import type { Hud, ObjectiveHudView, TeamTag } from "../hud";
import { formatTimer } from "../hud/format";
import { TICK_RATE } from "../net/constants";
import type { MapFileJson, Renderer } from "../render";
import { bindingLabel } from "../settings/keybinds";
import type { BombSnapData, HillSnapData, ModeSnapData, Vec3Like } from "../wasm/types";
import { relativeBearing } from "./convert";

type Box = { min: [number, number, number]; max: [number, number, number] };

/** Seconds before a hill takes over that the next one is shown (`HILL_PREVIEW_TICKS` of the server). */
export const HILL_PREVIEW_SEC = 5;
/** A defender defuses within this horizontal distance of the bomb (`DEFUSE_RANGE` of the server), metres. */
export const DEFUSE_RANGE_M = 1.5;

type Tone = ObjectiveHudView["tone"];

function center(b: Box): Vec3Like {
  return {
    x: (b.min[0] + b.max[0]) / 2,
    y: (b.min[1] + b.max[1]) / 2,
    z: (b.min[2] + b.max[2]) / 2,
  };
}

/** Standing in a hill or a bomb site: inside its footprint, at most half a metre below its floor and not above its top (`in_zone` of the server). */
function inside(b: Box, p: Vec3Like): boolean {
  return (
    p.x >= b.min[0] && p.x <= b.max[0] && p.z >= b.min[2] && p.z <= b.max[2] && p.y >= b.min[1] - 0.5 && p.y <= b.max[1]
  );
}

export interface ObjectiveContext {
  mode: "ffa" | "tdm" | "koth" | "defusal";
  myId: number;
  myTeam: TeamTag;
  /** Local feet position, null while dead. */
  feet: Vec3Like | null;
  eye: Vec3Like;
  yaw: number;
  /** Server tick now (estimated). */
  nowTick: number;
  /** The freeze time of a Defusal round: nothing can be done yet. */
  frozen: boolean;
  /** Binding of the interact action ("KeyE"), for the hints. */
  interactBinding: string;
}

function sameBomb(a: BombSnapData | null, b: BombSnapData | null): boolean {
  if (a === null || b === null) return a === b;
  const x = a.state;
  const y = b.state;
  if (x.kind !== y.kind) return false;
  if ((x.kind === "dropped" && y.kind === "dropped") || (x.kind === "planted" && y.kind === "planted")) {
    return x.pos.x === y.pos.x && x.pos.y === y.pos.y && x.pos.z === y.pos.z;
  }
  return true;
}

/** What the HUD shows about the hill in play. */
export interface HillStatus {
  label: string;
  /** Clock or the announcement of the next hill. */
  sub: string;
  tone: Tone;
  /** Index of the hill to outline as the next one, null outside the announcement. */
  preview: number | null;
  /** Changes whenever `label` or `sub` would (cache key, no string building to find out). */
  key: number;
}

/**
 * Text and tone of the hill line. `h.moveTick` is the tick the hill starts scoring (not active)
 * or hands over to the next one (active). With one hill only there is no next one.
 */
export function hillStatus(h: HillSnapData, myTeam: TeamTag, nowTick: number, hillCount: number): HillStatus {
  const secs = Math.max(0, Math.ceil((h.moveTick - nowTick) / TICK_RATE));
  const n = h.index + 1;
  if (!h.active) {
    return { label: `HILL ${n}`, sub: `OPENS IN ${secs} S`, tone: "neutral", preview: null, key: 1000 + secs };
  }
  // 0 empty, 1 held by us, 2 held by the enemy, 3 contested
  let state: number;
  let word: string;
  let tone: Tone;
  if (h.contested) {
    state = 3;
    word = "CONTESTED";
    tone = "contested";
  } else if (h.owner === "none") {
    state = 0;
    word = "EMPTY";
    tone = "neutral";
  } else if (h.owner === myTeam) {
    state = 1;
    word = "HELD";
    tone = "good";
  } else {
    state = 2;
    word = "ENEMY";
    tone = "bad";
  }
  const next = hillCount > 1 ? (h.index + 1) % hillCount : null;
  const previewing = next !== null && secs <= HILL_PREVIEW_SEC;
  const sub = previewing ? `NEXT: HILL ${(next as number) + 1} IN ${secs} S` : `MOVES IN ${formatTimer(secs)}`;
  return {
    label: `HILL ${n} \u00b7 ${word}`,
    sub,
    tone,
    preview: previewing ? next : null,
    key: 2000 + h.index * 100000 + state * 10000 + (previewing ? 5000 : 0) + secs,
  };
}

export class ObjectiveTracker {
  private hillCenters: Vec3Like[] = [];
  private sites: Box[] = [];
  private hill: HillSnapData | null = null;
  private bomb: BombSnapData | null = null;
  private lastBombPos: Vec3Like | null = null;
  private readonly view: ObjectiveHudView = {
    kind: "hill",
    label: "",
    tone: "neutral",
    dirRad: null,
    distanceM: null,
    progress: null,
    sub: null,
  };
  private shown = false;
  private preview: number | null = null;
  /** Last hill / bomb text and the key it was built for (no string building per frame). */
  private textKey = -1;
  private bombKind = "";
  private bombA = -1;
  private bombB = -1;
  private bombRole = "";
  private labelText = "";
  private subText: string | null = null;
  private keyBinding = "";
  private keyLabel = "";

  constructor(
    private readonly renderer: Renderer,
    private readonly hud: Hud,
  ) {}

  load(map: MapFileJson, mode: ObjectiveContext["mode"]): void {
    this.hillCenters = map.hills.map(center);
    this.sites = map.bombSites;
    this.setMode(mode);
  }

  /** The host switched the mode without a new map: bomb sites are only shown in Defusal. */
  setMode(mode: ObjectiveContext["mode"]): void {
    this.renderer.objectives.setBombSitesVisible(mode === "defusal");
  }

  clear(): void {
    this.hill = null;
    this.bomb = null;
    this.lastBombPos = null;
    this.shown = false;
    this.preview = null;
    this.textKey = -1;
    this.bombKind = "";
    this.renderer.objectives.clear();
    this.hud.setObjective(null);
    this.hud.interact(null, null);
  }

  /** Position the bomb exploded at (the last planted position). */
  bombPos(): Vec3Like | null {
    return this.lastBombPos;
  }

  /** New mode snapshot: updates the world markers when something changed. */
  onSnapshot(mode: ModeSnapData): void {
    const h = mode.hill;
    const hillChanged =
      (h === null) !== (this.hill === null) ||
      (h !== null && this.hill !== null && (h.index !== this.hill.index || h.owner !== this.hill.owner || h.contested !== this.hill.contested));
    this.hill = h;
    if (hillChanged) {
      if (h) this.renderer.objectives.setHill(h.index, h.owner, h.contested);
      else this.renderer.objectives.setHill(null, "none", false);
    }
    const b = mode.bomb;
    const bombChanged = !sameBomb(b, this.bomb);
    this.bomb = b;
    if (b?.state.kind === "planted") this.lastBombPos = b.state.pos;
    if (bombChanged) this.syncBomb();
  }

  private syncBomb(): void {
    const s = this.bomb?.state;
    if (s?.kind === "planted") this.renderer.objectives.setBomb({ pos: s.pos, planted: true });
    else if (s?.kind === "dropped") this.renderer.objectives.setBomb({ pos: s.pos, planted: false });
    else this.renderer.objectives.setBomb(null);
  }

  /** "E" for "KeyE" (rebuilt only when the binding changes). */
  private interactKey(binding: string): string {
    if (binding !== this.keyBinding) {
      this.keyBinding = binding;
      this.keyLabel = binding === "" ? "INTERACT" : bindingLabel(binding).toUpperCase();
    }
    return this.keyLabel;
  }

  /** Rebuilds the HUD line and the interact prompt; call every frame (the arrow follows the view). */
  updateHud(c: ObjectiveContext, nameOf: (id: number) => string | null): void {
    this.updateInteract(c);
    const v = this.view;
    if (c.mode === "koth" && this.hill) {
      const h = this.hill;
      const st = hillStatus(h, c.myTeam, c.nowTick, this.hillCenters.length);
      if (st.preview !== this.preview) {
        this.preview = st.preview;
        this.renderer.objectives.setHillPreview(st.preview);
      }
      if (st.key !== this.textKey || this.bombKind !== "hill") {
        this.textKey = st.key;
        this.bombKind = "hill";
        this.labelText = st.label;
        this.subText = st.sub;
      }
      v.kind = "hill";
      v.tone = st.tone;
      v.label = this.labelText;
      v.sub = this.subText;
      this.aim(v, this.hillCenters[h.index] ?? null, c);
      v.progress = null;
      this.push(v);
      return;
    }
    if (this.preview !== null) {
      this.preview = null;
      this.renderer.objectives.setHillPreview(null);
    }
    if (c.mode === "defusal" && this.bomb) {
      const b = this.bomb;
      v.kind = "bomb";
      v.progress = b.progress > 0 ? b.progress / 255 : null;
      const s = b.state;
      const attacker = c.myTeam === "a";
      let target: Vec3Like | null = null;
      if (s.kind === "carried") {
        const mine = s.carrier === c.myId;
        // Only the attackers learn who carries the bomb.
        const role = mine ? "me" : attacker ? "mate" : "enemy";
        if (this.bombKind !== "carried" || this.bombA !== s.carrier || this.bombRole !== role) {
          this.bombKind = "carried";
          this.bombA = s.carrier;
          this.bombRole = role;
          this.labelText = mine ? "YOU HAVE THE BOMB" : attacker ? `BOMB: ${(nameOf(s.carrier) ?? "carried").toUpperCase()}` : "BOMB CARRIED";
          this.subText = mine ? `HOLD ${this.interactKey(c.interactBinding)} ON A SITE TO PLANT` : null;
        }
        v.tone = mine ? "good" : "neutral";
      } else if (s.kind === "dropped") {
        if (this.bombKind !== "dropped" || this.bombRole !== (attacker ? "a" : "b")) {
          this.bombKind = "dropped";
          this.bombRole = attacker ? "a" : "b";
          this.labelText = "BOMB DROPPED";
          this.subText = attacker ? "WALK OVER IT TO PICK IT UP" : null;
        }
        v.tone = "contested";
        target = s.pos;
      } else if (s.kind === "planted") {
        const left = Math.max(0, Math.ceil((s.explodeTick - c.nowTick) / TICK_RATE));
        if (this.bombKind !== "planted" || this.bombA !== s.site || this.bombB !== left) {
          this.bombKind = "planted";
          this.bombA = s.site;
          this.bombB = left;
          this.labelText = `BOMB PLANTED \u00b7 SITE ${s.site === 0 ? "A" : "B"}`;
          this.subText = `EXPLODES IN ${formatTimer(left)}`;
        }
        v.tone = attacker ? "good" : "bad";
        target = s.pos;
      } else {
        if (this.bombKind !== s.kind) {
          this.bombKind = s.kind;
          this.labelText = s.kind === "defused" ? "BOMB DEFUSED" : "BOMB EXPLODED";
          this.subText = null;
        }
        v.tone = "neutral";
      }
      v.label = this.labelText;
      v.sub = this.subText;
      this.aim(v, target, c);
      this.push(v);
      return;
    }
    if (this.shown) {
      this.shown = false;
      this.hud.setObjective(null);
    }
  }

  private aim(v: ObjectiveHudView, target: Vec3Like | null, c: ObjectiveContext): void {
    if (!target) {
      v.dirRad = null;
      v.distanceM = null;
      return;
    }
    const dx = target.x - c.eye.x;
    const dz = target.z - c.eye.z;
    v.dirRad = relativeBearing(dx, dz, c.yaw);
    v.distanceM = Math.hypot(dx, dz);
  }

  private push(v: ObjectiveHudView): void {
    this.shown = true;
    this.hud.setObjective(v);
  }

  private insideSite(p: Vec3Like): boolean {
    for (const site of this.sites) if (inside(site, p)) return true;
    return false;
  }

  /** "Hold E" prompt: plant on a site while carrying, defuse next to a planted bomb. */
  private updateInteract(c: ObjectiveContext): void {
    const b = this.bomb;
    const feet = c.feet;
    if (c.mode !== "defusal" || !b || !feet || c.frozen) {
      this.hud.interact(null, null);
      return;
    }
    const s = b.state;
    const active = b.actor === c.myId;
    if (s.kind === "carried" && s.carrier === c.myId && this.insideSite(feet)) {
      this.hud.interact(active ? "Planting..." : `Hold ${this.interactKey(c.interactBinding)} to plant`, active ? b.progress / 255 : null);
    } else if (s.kind === "planted" && c.myTeam === "b" && Math.hypot(s.pos.x - feet.x, s.pos.z - feet.z) <= DEFUSE_RANGE_M) {
      this.hud.interact(active ? "Defusing..." : `Hold ${this.interactKey(c.interactBinding)} to defuse`, active ? b.progress / 255 : null);
    } else {
      this.hud.interact(null, null);
    }
  }
}
